import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { TableCell } from '@ovComponents/2-component/table/table';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MICRO_SX } from '../styles';

type EditableTableCellProps = {
  value: string | number;
  onSave: (newValue: number) => void;
  formatDisplay?: (value: string | number) => string;
  editable?: boolean;
  number?: boolean;
  width?: string;
  sx?: object;
};

const EditableTableCell = ({
  value,
  onSave,
  formatDisplay,
  editable = true,
  number: isNumber,
  width,
  sx = {},
}: EditableTableCellProps) => {
  const { sys } = useThemeTokens();
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const displayText = formatDisplay ? formatDisplay(value) : String(value);

  const handleDoubleClick = useCallback(() => {
    if (!editable) return;
    setEditValue(String(value));
    setEditing(true);
  }, [editable, value]);

  const commit = useCallback(() => {
    const parsed = parseFloat(editValue);
    setEditing(false);
    if (!isNaN(parsed) && parsed !== value) {
      onSave(parsed);
    }
  }, [editValue, value, onSave]);

  const revert = useCallback(() => {
    setEditing(false);
    setEditValue(String(value));
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        revert();
      }
    },
    [commit, revert],
  );

  const isChanged = editValue !== String(value);

  if (editing) {
    return (
      <TableCell number={isNumber} sx={{ py: 0.25, width, ...sx }}>
        <Box display='flex' alignItems='center' gap='2px'>
          <input
            type='text'
            value={editValue}
            ref={inputRef}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: `${Math.max(editValue.length, 4)}ch`,
              maxWidth: '100%',
              border: 'none',
              borderBottom: `1.5px solid ${isChanged ? sys.color.primary : sys.color.outlineVariant}`,
              outline: 'none',
              background: 'transparent',
              fontSize: '0.8125rem',
              fontWeight: 700,
              fontFamily: 'inherit',
              padding: '1px 0',
              textAlign: isNumber ? 'right' : 'left',
              color: 'inherit',
            }}
          />
          <Box
            onClick={commit}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              borderRadius: '2px',
              p: '1px',
              '&:hover': { bgcolor: sys.color.positiveVariant },
            }}
          >
            <CheckIcon sx={{ fontSize: 13, color: sys.color.positive }} />
          </Box>
          <Box
            onClick={revert}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              borderRadius: '2px',
              p: '1px',
              '&:hover': { bgcolor: sys.color.negativeVariant },
            }}
          >
            <CloseIcon sx={{ fontSize: 13, color: sys.color.negative }} />
          </Box>
        </Box>
        {isChanged && (
          <Typography
            variant='labelSmall'
            sx={{
              ...MICRO_SX,
              color: sys.color.onDisabled,
              lineHeight: 1,
              mt: '1px',
              textAlign: isNumber ? 'right' : 'left',
            }}
          >
            was {displayText}
          </Typography>
        )}
      </TableCell>
    );
  }

  return (
    <TableCell
      number={isNumber}
      sx={{
        py: 0.5,
        width,
        cursor: editable ? 'default' : undefined,
        ...sx,
      }}
      onDoubleClick={handleDoubleClick}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          ...(editable
            ? {
                borderBottom: '1px dashed',
                borderColor: 'divider',
                '&:hover': { borderColor: 'text.secondary' },
              }
            : {}),
        }}
      >
        <Typography variant='bodySmall' weight='bold'>
          {displayText}
        </Typography>
      </Box>
    </TableCell>
  );
};

export default EditableTableCell;
